/**
 * CarDetailModal.js — Finestra di dettaglio auto (sito pubblico)
 *
 * Si apre cliccando su una CarCard nel catalogo pubblico. Mostra il
 * carosello foto, la descrizione inserita dall'admin (campo `descrizione`,
 * opzionale) e le specifiche del veicolo, con una CTA "Prenota ora" che
 * chiude questa finestra e apre il BookingModal (gestito dal genitore,
 * tramite la prop onBook) — così il flusso di prenotazione resta unico e
 * non viene duplicato qui dentro.
 */

import React from 'react';
import { X, Car, CalendarCheck, Fuel, Palette, Tag } from 'lucide-react';
import ImageCarousel from './ImageCarousel';
import { getDisplayPrice } from '../utils/helpers';
import { useModal } from '../hooks/useModal';

const MotoIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" />
    <path d="M15 6l-2 4H8.5L7 13.5" /><path d="M15 6h3l1.5 4H15" />
    <path d="M8.5 10L6 17.5" /><path d="M15 10l1.5 4" />
  </svg>
);

const CarDetailModal = ({ car, onClose, onBook, showBookButton = false }) => {
  // Audit 2026-09-20, punto #11: useModal chiamato PRIMA dell'early return
  // qui sotto (regola React sugli hook: numero e ordine devono restare
  // identici ad ogni render). `active: !!car` lo disattiva quando non c'è
  // nulla da mostrare.
  const modalRef = useModal(onClose, !!car);
  if (!car) return null;
  const isMoto = car.tipoVeicolo === 'moto';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cardetail-modal-title"
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto flex flex-col rounded-t-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-5 sm:px-6 py-4 flex justify-between items-start rounded-t-2xl">
          <div>
            <h2 id="cardetail-modal-title" className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">{car.marca} {car.modello}</h2>
            <div className="flex items-center flex-wrap gap-2 mt-1.5">
              {car.categoria && (
                <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{car.categoria}</span>
              )}
              {car.targa && <span className="text-xs font-mono font-bold text-slate-400">{car.targa}</span>}
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                car.disponibile ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${car.disponibile ? 'bg-green-500' : 'bg-red-500'}`}></span>
                {car.disponibile ? 'Disponibile' : 'Non disponibile'}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 flex-1">
          {/* Foto */}
          {car.immagini && car.immagini.length > 0 ? (
            <ImageCarousel images={car.immagini} thumbs={car.immaginiThumb} title={`${car.marca} ${car.modello}`} />
          ) : (
            <div className="w-full h-56 bg-slate-50 rounded-2xl flex items-center justify-center">
              {isMoto
                ? <MotoIcon className="w-16 h-16 text-slate-200" />
                : <Car className="w-16 h-16 text-slate-200" />}
            </div>
          )}

          {/* Prezzo */}
          <div className="mt-5 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
            <span className="text-sm font-semibold text-slate-500">Prezzo</span>
            <p className="font-black text-[var(--brand,#2563EB)] text-lg leading-none">
              €{getDisplayPrice(car)}
              <span className="text-xs font-medium text-slate-400"> /giorno</span>
            </p>
          </div>

          {/* Specifiche */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            {car.categoria && (
              <div className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                <Tag className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Classe</p>
                <p className="text-xs font-semibold text-slate-700 capitalize">{car.categoria}</p>
              </div>
            )}
            {car.colore && (
              <div className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                <Palette className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Colore</p>
                <p className="text-xs font-semibold text-slate-700">{car.colore}</p>
              </div>
            )}
            {car.alimentazione && (
              <div className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                <Fuel className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{isMoto ? 'Cilindrata' : 'Motore'}</p>
                <p className="text-xs font-semibold text-slate-700">{isMoto && car.cilindrata ? `${car.cilindrata}cc` : car.alimentazione}</p>
              </div>
            )}
          </div>

          {/* Descrizione */}
          {car.descrizione && car.descrizione.trim() && (
            <div className="mt-5">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Descrizione</p>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{car.descrizione}</p>
            </div>
          )}
        </div>

        {/* Footer con CTA */}
        {showBookButton && (
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 sm:px-6 py-4">
            <button
              onClick={() => onBook && onBook(car)}
              disabled={!car.disponibile}
              className="w-full py-3.5 rounded-2xl font-black text-sm tracking-wide transition active:scale-95 flex items-center justify-center gap-2"
              style={car.disponibile
                // BUG FIX (accessibilità/contrasto colore personalizzato,
                // stesso bug corretto in PublicCarList.js per i pulsanti
                // AUTO/MOTO): 'color: #fff' fisso su sfondo var(--brand,...)
                // diventa illeggibile se l'azienda sceglie un accento chiaro.
                // --brand-on è la stessa variabile di contrasto già definita
                // sul contenitore radice del sito pubblico (PublicCarList.js).
                ? { background: 'var(--brand,#2563EB)', color: 'var(--brand-on,#fff)' }
                : { background: '#E2E8F0', color: '#94A3B8', cursor: 'not-allowed' }}
            >
              <CalendarCheck className="w-4 h-4" />
              {car.disponibile ? 'Prenota ora' : 'Non disponibile'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarDetailModal;
